import React, { useEffect, useState, useRef } from "react";
import { Modal, Typography, Card, Divider } from "antd";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { inject, observer } from "mobx-react";
import { isEmpty, uniqBy } from "lodash";

import AppButton from "components/AppButton/AppButton";
import TestMessageContactSelector from "../ContactSelector/TestMessageContactSelector";
import { validAusNumberRegExpDeluxe, getFormatedContact, getFormatedContacts } from "constant/messageConstants";

import style from "./TestMessage.module.css";
import styles from "assets/css/modals.module.css";

const { Text } = Typography;

const validationSchema = Yup.object().shape({
  contacts: Yup.array()
    .of(
      Yup.string()
        .matches(validAusNumberRegExpDeluxe, "Invalid phone number")
        .nullable(true)
    )
    .min(1, "Please add at least one number")
    .max(20, "Maximum 20 numbers allowed")
    .nullable(true),
  currentContact: Yup.string()
    .matches(validAusNumberRegExpDeluxe, "Invalid phone number format")
    .nullable(true)
});

const TestMessage = ({
  isVisible,
  handleCancel,
  sendGatewayMessage,
  authStore,
  modalStore,
  contactStore,
  testMessageStore,
  isMms = false,
  name = "message",
  skip = false,
  isFirstTime = false,
  values = {},
  saveAndContinue,
  initializeValue,
  saveToLocalStorage,
  setContactAdminModal,
  history,
  quickMessage,
  location
}) => {
  const { user } = authStore;
  const formikRef = useRef();

  const [contactCount, setContactCount] = useState(0);
  const [contactData, setContactData] = useState([]);
  const [balanceError, setBalanceError] = useState("");
  const formatPhoneForStorage = phone => {
    if (!phone) return phone;

    const cleanNumber = phone.replace(/[^\d+]/g, "");

    if (cleanNumber.startsWith("+61")) {
      return cleanNumber.substring(1);
    } else if (cleanNumber.startsWith("61") && cleanNumber.length >= 11) {
      return cleanNumber;
    } else if (cleanNumber.startsWith("0") && cleanNumber.length === 10) {
      return `61${cleanNumber.substring(1)}`;
    } else if (cleanNumber.length === 9) {
      return `61${cleanNumber}`;
    }

    return phone.replace(/^\+/, "");
  };

  const createUserContactObject = () => {
    if (!user?.mobile) return null;

    const formattedMobile = formatPhoneForStorage(user.mobile);

    const userContactData = {
      "first name": user.firstName || "",
      "last name": user.lastName || "",
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      first_name: user.firstName || "",
      last_name: user.lastName || ""
    };

    return createContactObject(formattedMobile, userContactData);
  };

  const createContactObject = (phoneNumber, contactData = {}) => {
    return {
      label: phoneNumber,
      value: phoneNumber,
      contact: phoneNumber,
      contactData: contactData
    };
  };

  const getRecentContactName = contact => {
    if (typeof contact === "object" && contact.contactData) {
      const { "first name": firstName, "last name": lastName } = contact.contactData;
      return `${firstName || ""} ${lastName || ""}`.trim() || null;
    }

    const recentContact = testMessageStore.recentNumbers?.find(c => c.phoneNumber === contact);
    if (recentContact) {
      const firstName = recentContact.contactData?.["first name"] || "";
      const lastName = recentContact.contactData?.["last name"] || "";
      const fullName = `${firstName} ${lastName}`.trim();
      return fullName || null;
    }
    return null;
  };

  const validateContactBalance = contactsArray => {
    const totalMessagesCount = testMessageStore.totalMessagesCount || 1;
    const selectedNumberSMSCount = totalMessagesCount * contactsArray.length;

    const balanceRaw = localStorage.getItem("balance");
    let totalSMSCount = 0;

    if (balanceRaw) {
      try {
        const balance = JSON.parse(balanceRaw);
        totalSMSCount = balance.value.maxPossibleCredits.sms || 0;
      } catch (e) {
        console.error("Failed to parse balance from localStorage:", e);
      }
    }

    const maxAllowedContacts = Math.floor(totalSMSCount / totalMessagesCount);

    if (selectedNumberSMSCount > totalSMSCount) {
      setBalanceError(`You can send to ${maxAllowedContacts} contact${maxAllowedContacts !== 1 ? "s" : ""} only. Add credits for more.`);
      return false;
    } else {
      setBalanceError("");
      return true;
    }
  };

  const canAddMoreContacts = currentContacts => {
    const totalMessagesCount = testMessageStore.totalMessagesCount || 1;
    const balanceRaw = localStorage.getItem("balance");
    let totalSMSCount = 0;

    if (balanceRaw) {
      try {
        const balance = JSON.parse(balanceRaw);
        totalSMSCount = balance.value.maxPossibleCredits.sms || 0;
      } catch (e) {
        console.error("Failed to parse balance from localStorage:", e);
      }
    }

    const maxAllowedContacts = Math.floor(totalSMSCount / totalMessagesCount);
    return currentContacts.length < maxAllowedContacts;
  };

  const handleAddCredits = async (currentValues, isSubmitting) => {
    try {
      if (saveToLocalStorage && typeof saveToLocalStorage === "function") {
        await saveToLocalStorage(currentValues, "initial", isSubmitting);
      }
      if (user?.role === "subaccount") {
        if (setContactAdminModal && typeof setContactAdminModal === "function") {
          setContactAdminModal(true);
        } else {
          console.warn("setContactAdminModal function not available");
          alert("Please contact your admin to add credits.");
        }
      } else {
        if (history && location) {
          history.push("/buy-credits", { from: location.pathname });
        } else {
          console.warn("history or location not available, trying alternative navigation");
          if (window.location) {
            window.location.href = "/buy-credits";
          } else {
            console.error("No navigation method available");
            alert("Please navigate to the buy credits page manually.");
          }
        }
      }
    } catch (error) {
      console.error("Error in handleAddCredits:", error);
      alert("There was an error processing your request. Please try again.");
    }
  };

  const mapRecentContactData = recentContact => {
    if (!recentContact.contactData) return {};

    const mappedData = {};

    Object.keys(recentContact.contactData).forEach(key => {
      if (!key.includes("_") && !mappedData[key]) {
        mappedData[key] = recentContact.contactData[key];
      }
    });

    return mappedData;
  };

  const updateNumberList = (contacts, setFieldValue, currentNumberList = []) => {
    const newNumberList = contacts.map(contact => {
      const existingContact = currentNumberList.find(item => item.value === contact);
      if (existingContact) {
        return existingContact;
      }

      let contactData = {};

      const recentContact = testMessageStore.recentNumbers?.find(c => c.phoneNumber === contact);
      if (recentContact && contact === formatPhoneForStorage(user?.mobile)) {
        contactData = mapRecentContactData(recentContact);
      } else if (contact === formatPhoneForStorage(user?.mobile) && user?.firstName) {
        contactData = {
          "first name": user.firstName || "",
          "last name": user.lastName || "",
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          first_name: user.firstName || "",
          last_name: user.lastName || ""
        };
      } else if (recentContact) {
        contactData = mapRecentContactData(recentContact);
      }

      return createContactObject(contact, contactData);
    });

    const uniqueNumberList = uniqBy(newNumberList, "value");
    setFieldValue("numberList", uniqueNumberList);
  };

  const setDefaultContacts = async () => {
    try {
      let defaultContacts = [];

      if (modalStore.quickSMSContact?.phoneNumber) {
        defaultContacts = getFormatedContacts([modalStore.quickSMSContact.phoneNumber]);
      } else if (user?.mobile && ((isMms && user.firstQuickMMS) || (!isMms && user.firstQuickSMS))) {
        const existingContacts = formikRef.current?.values?.contacts || [];
        const formattedUserMobile = formatPhoneForStorage(user.mobile);
        defaultContacts = [...getFormatedContacts([formattedUserMobile]), ...existingContacts].filter((item, index, self) => self.indexOf(item) === index);
      } else {
        defaultContacts = formikRef.current?.values?.contacts || [];
      }

      if (isEmpty(defaultContacts) && user?.mobile) {
        defaultContacts = [formatPhoneForStorage(user.mobile)];
      }

      const userNumber = user?.mobile ? formatPhoneForStorage(user.mobile) : null;
      if (userNumber) {
        const filteredNumbers = defaultContacts.filter(num => num !== userNumber);
        defaultContacts = [userNumber, ...filteredNumbers];
      }

      validateContactBalance(defaultContacts);

      if (formikRef.current) {
        const initialNumberList = [];
        if (user?.mobile && user?.firstName) {
          const recentContact = testMessageStore.recentNumbers?.find(c => c.phoneNumber === formatPhoneForStorage(user.mobile));

          let userContactObj;
          if (recentContact) {
            const recentContactData = mapRecentContactData(recentContact);
            userContactObj = createContactObject(formatPhoneForStorage(user.mobile), recentContactData.contactData);
          } else {
            userContactObj = createUserContactObject();
          }

          if (userContactObj) {
            initialNumberList.push(userContactObj);
          }
        }

        formikRef.current.setFieldValue("numberList", initialNumberList);
        updateNumberList(defaultContacts, formikRef.current.setFieldValue, initialNumberList);
      }
    } catch (error) {
      console.error("Error setting default contacts:", error);
    }
  };

  useEffect(() => {
    if (isVisible) {
      initializeComponent();
    }
  }, [isVisible]);

  const initializeComponent = async () => {
    try {
      if (user?.mobile) {
        modalStore.setData("quickSMSContact", {
          phoneNumber: getFormatedContact(user.mobile),
          "first name": user.firstName || "",
          "last name": user.lastName || ""
        });
      }

      await testMessageStore.fetchRecentNumbers();

      if (testMessageStore.recentNumbers?.length) {
        testMessageStore.recentNumbers = testMessageStore.recentNumbers.map(r => ({
          ...r,
          phoneNumber: formatPhoneForStorage(r.phoneNumber)
        }));
      }

      await setDefaultContacts();
    } catch (error) {
      console.error("Error initializing component:", error);
    }
  };

  const handleAddRecentContact = (number, setFieldValue, currentContacts) => {
    const formattedNumber = formatPhoneForStorage(number);

    if (!currentContacts.includes(number)) {
      if (!canAddMoreContacts(currentContacts)) {
        const totalMessagesCount = testMessageStore.totalMessagesCount || 1;
        const balanceRaw = localStorage.getItem("balance");
        let totalSMSCount = 0;

        if (balanceRaw) {
          try {
            const balance = JSON.parse(balanceRaw);
            totalSMSCount = balance.value.maxPossibleCredits.sms || 0;
          } catch (e) {
            console.error("Failed to parse balance from localStorage:", e);
          }
        }

        const maxAllowedContacts = Math.floor(totalSMSCount / totalMessagesCount);
        setBalanceError(`You can send to ${maxAllowedContacts} contact${maxAllowedContacts !== 1 ? "s" : ""} only. Add credits to send to more.`);
        return;
      }

      const updatedContacts = [...currentContacts, number];
      setFieldValue("contacts", updatedContacts);
      validateContactBalance(updatedContacts);

      updateNumberList(updatedContacts, setFieldValue, formikRef.current?.values?.numberList || []);
    }
  };

  const handleContactDataChange = contacts => {
    setContactData(contacts);

    if (formikRef.current) {
      const updatedNumberList = contacts.map(contact => {
        const existingData = contact.contactData || {};

        const foundRecentNumber = testMessageStore.recentNumbers?.find(c => c.phoneNumber === contact.number);

        const mergedData = foundRecentNumber?.contactData ? { ...existingData, ...foundRecentNumber.contactData } : existingData;

        const normalizedData = {
          ...mergedData,
          "first name": mergedData["first name"] || mergedData.firstName || mergedData.first_name || "",
          "last name": mergedData["last name"] || mergedData.lastName || mergedData.last_name || "",
          firstName: mergedData.firstName || mergedData.first_name || mergedData["first name"] || "",
          lastName: mergedData.lastName || mergedData.last_name || mergedData["last name"] || "",
          first_name: mergedData.first_name || mergedData.firstName || mergedData["first name"] || "",
          last_name: mergedData.last_name || mergedData.lastName || mergedData["last name"] || ""
        };

        return {
          ...contact,
          contactData: normalizedData
        };
      });

      formikRef.current.setFieldValue("numberList", updatedNumberList);
    }
  };

  const handleContactSelection = (newContacts, setFieldValue, currentContacts) => {
    const numberList = formikRef.current?.values?.numberList || [];

    const contactsWithData = numberList.map(contact => ({
      number: contact.value,
      first_name: contact.contactData?.first_name || "",
      last_name: contact.contactData?.last_name || "",
      label: contact.label
    }));

    if (!validateContactBalance(newContacts)) {
      const totalMessagesCount = testMessageStore.totalMessagesCount || 1;
      const balanceRaw = localStorage.getItem("balance");
      let totalSMSCount = 0;

      if (balanceRaw) {
        try {
          const balance = JSON.parse(balanceRaw);
          totalSMSCount = balance.value.maxPossibleCredits.sms || 0;
        } catch (e) {
          console.error("Failed to parse balance from localStorage:", e);
        }
      }

      const maxAllowedContacts = Math.floor(totalSMSCount / totalMessagesCount);
      const validContacts = newContacts.slice(0, maxAllowedContacts);
      setFieldValue("contacts", validContacts);
      updateNumberList(validContacts, setFieldValue, formikRef.current?.values?.numberList || []);
      return;
    }

    setFieldValue("contacts", newContacts);
    updateNumberList(newContacts, setFieldValue, formikRef.current?.values?.numberList || []);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const testValues = { ...values };
      const fieldsToRemove = [
        "currentContact",
        "scheduleAt",
        "activeTab",
        "dayOfMonth",
        "daysOfWeek",
        "endDate",
        "isSchedule",
        "monthWeek",
        "monthWeekDay",
        "monthlyType",
        "repeat",
        "repeatInterval",
        "startDate",
        "sendType",
        "senders",
        "senderType",
        "start_date",
        "end_date",
        "repetitive",
        "status"
      ];
      fieldsToRemove.forEach(field => delete testValues[field]);

      testValues.isTest = true;

      await sendGatewayMessage(testValues);
      contactStore.setSearchContacts();

      if (!testValues.sender && user?.senderId) {
        testValues.sender = user.senderId;
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (formikRef.current?.resetForm) {
      modalStore.setReset(formikRef.current?.resetForm);
    }
  }, [modalStore.quickSMSContact]);

  useEffect(() => {
    if (formikRef.current?.values?.contacts) {
      validateContactBalance(formikRef.current.values.contacts);
    }
  }, [contactCount, testMessageStore.totalMessagesCount]);

  if (!user) return null;

  const initialValues = {
    ...values,
    contacts: user?.mobile ? [formatPhoneForStorage(user.mobile)] : [],
    currentContact: "",
    numberList: user?.mobile && user?.firstName ? [createUserContactObject()] : []
  };

  const recentNumbers = testMessageStore.recentNumbers || [];
  const selectedNumberSMSCount = testMessageStore.totalMessagesCount * contactCount;
  const balanceRaw = localStorage.getItem("balance");
  let totalSMSCount;
  if (balanceRaw) {
    try {
      const balance = JSON.parse(balanceRaw);
      totalSMSCount = balance.value.maxPossibleCredits.sms;
    } catch (e) {
      console.error("Failed to parse balance from localStorage:", e);
    }
  } else {
    console.warn("No balance found in localStorage.");
  }

  return (
    <Modal
      destroyOnClose
      centered
      title={null}
      closeIcon={
        <div className={`btn ${styles.modalCloseBtn}`}>
          <i className="fas fa-times"></i>
        </div>
      }
      visible={isVisible}
      onCancel={handleCancel}
      width={700}
      footer={null}
      bodyStyle={{ padding: 0, maxHeight: "90vh", display: "flex", flexDirection: "column" }}
    >
      <div className={style.header}>
        <Text className={style.headerTitle}>Test {isMms ? "MMS" : "SMS"}</Text>
        <AppButton onClick={handleCancel} withoutBG light prefixIcon={<i className="fas fa-times" style={{ color: "white" }} />} className={style.closeBtn} />
      </div>

      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} innerRef={formikRef}>
        {({ values, errors, touched, setFieldValue, handleSubmit, isSubmitting }) => (
          <div className={style.bodyContainer} style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
            <div className={style.scrollableContent} style={{ flex: 1, overflowY: "auto", paddingBottom: "10px" }}>
              <div>
                <Text className={style.sectionTitle}>To</Text>
                <div className={style.addContactRow}>
                  <div style={{ flex: 1 }}>
                    <TestMessageContactSelector
                      name="contacts"
                      placeholder="Enter mobile numbers "
                      values={values}
                      errors={errors}
                      touched={touched}
                      setFieldValue={setFieldValue}
                      onChange={newContacts => handleContactSelection(newContacts, setFieldValue, values.contacts)}
                      onCountChange={count => setContactCount(count)}
                      onContactDataChange={handleContactDataChange}
                      isTestSms={true}
                      containerClassname=""
                      inputClassname=""
                      filterContactBy="contacts"
                      contactDataField="numberList"
                    />
                  </div>
                </div>
              </div>

              {recentNumbers.length > 0 && (
                <>
                  <div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                    <i
                      className="fas fa-history"
                      style={{
                        marginRight: "8px",
                        color: "#149cbe",
                        fontSize: "16px"
                      }}
                    />
                    <Text className={style.sectionTitle}>Recent</Text>
                  </div>

                  <div className={style.recentContactsContainer}>
                    <div className={style.recentContactsList}>
                      {testMessageStore.isLoadingRecent ? (
                        <div style={{ textAlign: "center", padding: "5px" }}>
                          <Text className={style.recentContactNumber}>Contact loading</Text>
                        </div>
                      ) : (
                        recentNumbers.map((recentContact, index) => {
                          const number = recentContact.phoneNumber;
                          const isSelected = values.contacts.includes(number);
                          const mappedData = mapRecentContactData(recentContact);
                          const firstName = mappedData["first name"] || "";
                          const lastName = mappedData["last name"] || "";
                          const fullName = `${firstName} ${lastName}`.trim();
                          const contactName = fullName;

                          return (
                            <div
                              key={index}
                              className={`${style.recentContactItem} ${isSelected ? style.recentContactItemSelected : ""} ${!isSelected ? style.recentContactItemDisabled : ""}`}
                              onClick={() => {
                                if (!isSelected) {
                                  handleAddRecentContact(number, setFieldValue, values.contacts);
                                }
                              }}
                              style={{ cursor: isSelected ? "no-drop" : "pointer" }}
                            >
                              <div style={{ flex: 1 }}>
                                {/* {contactName && (
                                <Text className={`text-center ${style.recentContactName} ${isSelected ? style.recentContactNameSelected : ""}`}>
                                  {contactName}
                                </Text>
                              )}
                              <Divider clasame="m-0 p-0" /> */}
                                <Text className={`${style.recentContactNumber} ${isSelected ? style.recentContactNumberSelected : ""}`}>{number}</Text>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className={style.footer} style={{ flexShrink: 0, borderTop: "1px solid #e8e8e8", backgroundColor: "#fff", padding: "16px" }}>
              <div />
              <Form onSubmit={handleSubmit} className={style.footerForm}>
                {balanceError ? (
                  <div className={style.footerFormSubmit}>
                    <div className={style.footerFormSubmitError}>{balanceError}</div>
                    <div className={style.footerFormAddLabelButton}>
                      <AppButton
                        label={isFirstTime ? `Send Test ${isMms ? "MMS" : "SMS"}` : `Resend Test ${isMms ? "MMS" : "SMS"}`}
                        type="submit"
                        light
                        disabled={isSubmitting || values.contacts.length === 0 || !!balanceError}
                        isSubmitting={isSubmitting}
                      />
                      <AppButton label="Add Credits" light className="bg-danger-color border-danger-color" onClick={() => handleAddCredits(values, isSubmitting)} />
                    </div>
                  </div>
                ) : (
                  <div className={style.footerButtons}>
                    {(skip || saveAndContinue) && (
                      <AppButton
                        label={"Skip"}
                        type="button"
                        light
                        withoutBg
                        onClick={() => {
                          if (!quickMessage) {
                            saveAndContinue();
                          }
                          handleCancel();
                        }}
                      />
                    )}
                    <AppButton
                      label={isFirstTime ? `Send Test ${isMms ? "MMS" : "SMS"}` : `Resend Test ${isMms ? "MMS" : "SMS"}`}
                      type="submit"
                      light
                      disabled={isSubmitting || values.contacts.length === 0}
                      isSubmitting={isSubmitting}
                    />
                  </div>
                )}
              </Form>
            </div>
          </div>
        )}
      </Formik>
    </Modal>
  );
};

export default inject(stores => ({
  authStore: stores.store.authStore,
  modalStore: stores.store.modalStore,
  contactStore: stores.store.contactStore,
  testMessageStore: stores.store.testMessageStore
}))(observer(TestMessage));
